import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { 
  FileText, Play, Users, Building2, Clock, CheckCircle, XCircle,
  History, Download, AlertCircle, RefreshCw, Calendar, Search, X, Filter
} from 'lucide-react';
import { toast } from 'sonner';
import { StatementSanityCheckPanel } from '../../components/StatementSanityCheckPanel';
import { InvestorPortalDateCard } from '../../components/admin/InvestorPortalDateCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminStatementsPage = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('run');
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [clientStatements, setClientStatements] = useState([]);
  const [investorStatements, setInvestorStatements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [runningBatch, setRunningBatch] = useState(null);
  
  // Form state
  const [periodYear, setPeriodYear] = useState(new Date().getFullYear());
  const [periodMonth, setPeriodMonth] = useState(new Date().getMonth() + 1);
  const [statementType, setStatementType] = useState('monthly');
  const [sendNotifications, setSendNotifications] = useState(true);

  // Selection state
  const [runTarget, setRunTarget] = useState('clients'); // 'clients' | 'investors'
  const [selectionMode, setSelectionMode] = useState('all'); // 'all' | 'selected'
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('default');
  const [loanFilter, setLoanFilter] = useState('active');

  // Data for dropdowns
  const [selectableClients, setSelectableClients] = useState([]);
  const [selectableInvestors, setSelectableInvestors] = useState([]);
  const [selectableTemplates, setSelectableTemplates] = useState([]);
  const [loadingSelectable, setLoadingSelectable] = useState(false);

  // Multi-select for statement deletion
  const [selectedClientStmts, setSelectedClientStmts] = useState([]);
  const [selectedInvestorStmts, setSelectedInvestorStmts] = useState([]);

  const months = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' },
    { value: 3, label: 'March' }, { value: 4, label: 'April' },
    { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' },
    { value: 9, label: 'September' }, { value: 10, label: 'October' },
    { value: 11, label: 'November' }, { value: 12, label: 'December' }
  ];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  useEffect(() => {
    fetchStats();
    fetchHistory();
    fetchSelectableData();
  }, []);

  useEffect(() => {
    if (activeTab === 'clients') fetchClientStatements();
    else if (activeTab === 'investors') fetchInvestorStatements();
  }, [activeTab]);

  // Reset selection when switching target
  useEffect(() => {
    setSelectedUserIds([]);
    setSearchTerm('');
    // Fetch templates for the relevant type
    fetchTemplates(runTarget === 'clients' ? 'client_statement' : 'investor_statement');
  }, [runTarget]);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/statements/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error('Failed to fetch stats:', e); }
  };

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/statements/history?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setHistory(await res.json());
    } catch (e) { console.error('Failed to fetch history:', e); }
  };

  const fetchClientStatements = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/statements/client-statements?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setClientStatements(data.statements || []);
      }
    } catch (e) { console.error('Failed to fetch client statements:', e); }
  };

  const fetchInvestorStatements = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/statements/investor-statements?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setInvestorStatements(data.statements || []);
      }
    } catch (e) { console.error('Failed to fetch investor statements:', e); }
  };

  const fetchSelectableData = async () => {
    setLoadingSelectable(true);
    try {
      const [clientsRes, investorsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/statements/selectable-clients`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/statements/selectable-investors`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      if (clientsRes.ok) {
        const d = await clientsRes.json();
        setSelectableClients(d.clients || []);
      }
      if (investorsRes.ok) {
        const d = await investorsRes.json();
        setSelectableInvestors(d.investors || []);
      }
    } catch (e) { console.error('Failed to fetch selectable data:', e); }
    setLoadingSelectable(false);
    // Fetch initial templates
    fetchTemplates('client_statement');
  };

  const fetchTemplates = async (templateType) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/statements/selectable-templates?template_type=${templateType}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const d = await res.json();
        const templates = d.templates || [];
        setSelectableTemplates(templates);
        // Auto-select the default template, or first available
        const defaultTpl = templates.find(t => t.is_default);
        setSelectedTemplateId(defaultTpl ? defaultTpl.id : templates.length > 0 ? templates[0].id : 'default');
      }
    } catch (e) { console.error('Failed to fetch templates:', e); }
  };

  const currentUserList = runTarget === 'clients' ? selectableClients : selectableInvestors;

  const filteredUsers = useMemo(() => {
    if (!searchTerm) return currentUserList;
    const term = searchTerm.toLowerCase();
    return currentUserList.filter(u =>
      u.name?.toLowerCase().includes(term) ||
      u.id?.toLowerCase().includes(term) ||
      u.email?.toLowerCase().includes(term) ||
      u.client_id?.toLowerCase().includes(term)
    );
  }, [currentUserList, searchTerm]);

  const toggleUserSelection = (userId) => {
    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAll = () => {
    setSelectedUserIds(filteredUsers.map(u => u.id));
  };

  const deselectAll = () => {
    setSelectedUserIds([]);
  };

  const handleRunStatements = async () => {
    if (selectionMode === 'selected' && selectedUserIds.length === 0) {
      toast.error('No users selected', { description: 'Please select at least one user or switch to "All".' });
      return;
    }

    setLoading(true);
    const batchType = runTarget;
    setRunningBatch(batchType);

    const endpoint = runTarget === 'clients'
      ? '/api/admin/statements/run-client-statements'
      : '/api/admin/statements/run-investor-statements';

    const body = {
      period_year: periodYear,
      period_month: periodMonth,
      statement_type: statementType,
      send_notifications: sendNotifications,
      user_ids: selectionMode === 'selected' ? selectedUserIds : null,
      template_id: selectedTemplateId !== 'default' ? selectedTemplateId : null,
      loan_filter: runTarget === 'clients' ? loanFilter : 'all'
    };

    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (res.ok) {
        const result = await res.json();
        const label = runTarget === 'clients' ? 'Client' : 'Investor';
        toast.success(`${label} Statements Generated`, {
          description: `${result.successful} of ${result.total_processed} statements created for ${result.period}`
        });
        fetchStats();
        fetchHistory();
      } else {
        let errorMsg = `Server returned ${res.status}`;
        try {
          const error = await res.json();
          errorMsg = error.detail || errorMsg;
        } catch {
          const text = await res.text().catch(() => '');
          if (text) errorMsg = text.substring(0, 200);
        }
        toast.error('Failed to run statements', { description: errorMsg });
      }
    } catch (e) {
      toast.error('Error running statements', { description: e.message });
    } finally {
      setLoading(false);
      setRunningBatch(null);
    }
  };

  const handleDownload = async (statementId, type) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/statements/${statementId}/download?statement_type=${type}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        let errorMsg = `Server returned ${res.status}`;
        try {
          const err = await res.json();
          errorMsg = err.detail || errorMsg;
        } catch {
          const text = await res.text().catch(() => '');
          if (text) errorMsg = text.substring(0, 200);
        }
        throw new Error(errorMsg);
      }
      const data = await res.json();
      if (data.source === 'generated') {
        // Direct PDF generation endpoint - download via fetch
        const pdfRes = await fetch(`${API_URL}${data.download_url}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!pdfRes.ok) throw new Error('PDF generation failed');
        const blob = await pdfRes.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.file_name || `statement_${statementId}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        window.open(data.download_url, '_blank');
      }
    } catch (err) {
      toast.error('Download failed', { description: err.message });
    }
  };

  const handleView = async (statementId, type) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/statements/${statementId}/view?statement_type=${type}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Could not load statement');
      const html = await res.text();
      const blob = new Blob([html], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      toast.error('View failed', { description: err.message });
    }
  };

  const handleDelete = async (statementId, type) => {
    if (!window.confirm('Are you sure you want to delete this statement?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/statements/${statementId}?statement_type=${type}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        let errorMsg = `Server returned ${res.status}`;
        try {
          const error = await res.json();
          errorMsg = error.detail || errorMsg;
        } catch {
          const text = await res.text().catch(() => '');
          if (text) errorMsg = text.substring(0, 200);
        }
        throw new Error(errorMsg);
      }
      toast.success('Statement deleted');
      if (type === 'client') fetchClientStatements();
      else fetchInvestorStatements();
      fetchStats();
    } catch (err) {
      toast.error('Delete failed', { description: err.message });
    }
  };

  const handleBulkDelete = async (type) => {
    const ids = type === 'client' ? selectedClientStmts : selectedInvestorStmts;
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} selected statement${ids.length > 1 ? 's' : ''}?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/statements/bulk-delete-selected`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ statement_ids: ids, statement_type: type })
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      toast.success(`Deleted ${data.deleted} statement${data.deleted > 1 ? 's' : ''}`);
      if (type === 'client') { setSelectedClientStmts([]); fetchClientStatements(); }
      else { setSelectedInvestorStmts([]); fetchInvestorStatements(); }
      fetchStats();
    } catch (err) {
      toast.error('Bulk delete failed', { description: err.message });
    }
  };

  const toggleStmtSelection = (id, type) => {
    if (type === 'client') {
      setSelectedClientStmts(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    } else {
      setSelectedInvestorStmts(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    }
  };

  const toggleAllStmts = (type) => {
    if (type === 'client') {
      setSelectedClientStmts(prev => prev.length === clientStatements.length ? [] : clientStatements.map(s => s.id));
    } else {
      setSelectedInvestorStmts(prev => prev.length === investorStatements.length ? [] : investorStatements.map(s => s.id));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-ZA', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    } catch { return dateStr; }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency', currency: 'ZAR', minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(amount || 0);
  };

  return (
    <div className="space-y-6" data-testid="admin-statements-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Statement Management</h1>
          <p className="text-slate-600">Generate and manage client and investor statements</p>
        </div>
        <Button variant="outline" onClick={() => { fetchStats(); fetchHistory(); }} className="gap-2">
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      <InvestorPortalDateCard token={token} />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg"><Building2 className="h-5 w-5 text-blue-600" /></div>
              <div>
                <p className="text-sm text-slate-600">Client Statements</p>
                <p className="text-2xl font-bold">{stats?.totals?.client_statements || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg"><Users className="h-5 w-5 text-green-600" /></div>
              <div>
                <p className="text-sm text-slate-600">Investor Statements</p>
                <p className="text-2xl font-bold">{stats?.totals?.investor_statements || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg"><Calendar className="h-5 w-5 text-amber-600" /></div>
              <div>
                <p className="text-sm text-slate-600">This Month</p>
                <p className="text-2xl font-bold">
                  {(stats?.this_month?.client_statements || 0) + (stats?.this_month?.investor_statements || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg"><FileText className="h-5 w-5 text-purple-600" /></div>
              <div>
                <p className="text-sm text-slate-600">Total Statements</p>
                <p className="text-2xl font-bold">{stats?.totals?.all_statements || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="run" className="gap-2"><Play className="h-4 w-4" />Run Statements</TabsTrigger>
          <TabsTrigger value="history" className="gap-2"><History className="h-4 w-4" />Run History</TabsTrigger>
          <TabsTrigger value="clients" className="gap-2"><Building2 className="h-4 w-4" />Client Statements</TabsTrigger>
          <TabsTrigger value="investors" className="gap-2"><Users className="h-4 w-4" />Investor Statements</TabsTrigger>
        </TabsList>

        {/* ====== Run Statements Tab ====== */}
        <TabsContent value="run">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Col 1: Period & Options */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Statement Period</CardTitle>
                <CardDescription>Select the period and options</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Year</Label>
                    <Select value={periodYear.toString()} onValueChange={(v) => setPeriodYear(parseInt(v))}>
                      <SelectTrigger data-testid="year-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {years.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Month</Label>
                    <Select value={periodMonth.toString()} onValueChange={(v) => setPeriodMonth(parseInt(v))}>
                      <SelectTrigger data-testid="month-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {months.map(m => <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Statement Type</Label>
                  <Select value={statementType} onValueChange={setStatementType}>
                    <SelectTrigger data-testid="statement-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="annual">Annual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Target: Clients or Investors */}
                <div className="space-y-2">
                  <Label>Generate For</Label>
                  <Select value={runTarget} onValueChange={setRunTarget}>
                    <SelectTrigger data-testid="run-target-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="clients">
                        <span className="flex items-center gap-2"><Building2 className="h-4 w-4" /> Clients</span>
                      </SelectItem>
                      <SelectItem value="investors">
                        <span className="flex items-center gap-2"><Users className="h-4 w-4" /> Investors</span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Template selection */}
                <div className="space-y-2">
                  <Label>Template</Label>
                  <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                    <SelectTrigger data-testid="template-select"><SelectValue placeholder="Select Template" /></SelectTrigger>
                    <SelectContent>
                      {selectableTemplates.map(t => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name} {t.is_default && '(Default)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Loan filter (client statements only) */}
                {runTarget === 'clients' && (
                  <div className="space-y-2">
                    <Label>Loan Filter</Label>
                    <Select value={loanFilter} onValueChange={setLoanFilter}>
                      <SelectTrigger data-testid="loan-filter-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active Loans Only (default)</SelectItem>
                        <SelectItem value="all">All Loans (Active + Inactive)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="flex items-center space-x-2 pt-2">
                  <Checkbox id="notifications" checked={sendNotifications} onCheckedChange={setSendNotifications} data-testid="notifications-checkbox" />
                  <Label htmlFor="notifications" className="text-sm cursor-pointer">
                    Send notifications when statements are ready
                  </Label>
                </div>
              </CardContent>
            </Card>

            {/* Col 2: User Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Filter className="h-5 w-5" />
                  {runTarget === 'clients' ? 'Client' : 'Investor'} Selection
                </CardTitle>
                <CardDescription>
                  Choose specific {runTarget} or run for all
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Selection Mode Toggle */}
                <div className="flex gap-2">
                  <Button
                    variant={selectionMode === 'all' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setSelectionMode('all'); setSelectedUserIds([]); }}
                    data-testid="select-mode-all"
                    className="flex-1"
                  >
                    All {runTarget === 'clients' ? 'Clients' : 'Investors'}
                  </Button>
                  <Button
                    variant={selectionMode === 'selected' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectionMode('selected')}
                    data-testid="select-mode-individual"
                    className="flex-1"
                  >
                    Select Individual
                  </Button>
                </div>

                {selectionMode === 'all' ? (
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <Users className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-600">
                      Statements will be generated for <strong>all {currentUserList.length}</strong> {runTarget === 'clients' ? 'clients with active loans' : 'active investors'}
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Search */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder={`Search ${runTarget}...`}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9"
                        data-testid="user-search-input"
                      />
                      {searchTerm && (
                        <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 transform -translate-y-1/2">
                          <X className="h-4 w-4 text-slate-400 hover:text-slate-600" />
                        </button>
                      )}
                    </div>

                    {/* Select All / Deselect */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">
                        {selectedUserIds.length} of {filteredUsers.length} selected
                      </span>
                      <div className="flex gap-2">
                        <button onClick={selectAll} className="text-blue-600 hover:underline text-xs" data-testid="select-all-btn">Select All</button>
                        <button onClick={deselectAll} className="text-slate-500 hover:underline text-xs" data-testid="deselect-all-btn">Clear</button>
                      </div>
                    </div>

                    {/* User List */}
                    <div className="border rounded-lg max-h-64 overflow-y-auto" data-testid="user-selection-list">
                      {loadingSelectable ? (
                        <div className="p-4 text-center text-slate-500">Loading...</div>
                      ) : filteredUsers.length === 0 ? (
                        <div className="p-4 text-center text-slate-500">No {runTarget} found</div>
                      ) : (
                        filteredUsers.map(user => (
                          <label
                            key={user.id}
                            className={`flex items-center gap-3 px-3 py-2 cursor-pointer border-b last:border-b-0 hover:bg-slate-50 transition-colors ${
                              selectedUserIds.includes(user.id) ? 'bg-blue-50' : ''
                            }`}
                          >
                            <Checkbox
                              checked={selectedUserIds.includes(user.id)}
                              onCheckedChange={() => toggleUserSelection(user.id)}
                              data-testid={`select-user-${user.id}`}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{user.name}</p>
                              <p className="text-xs text-slate-500 truncate">
                                {user.rp_code || user.client_id || user.email || user.id}
                              </p>
                            </div>
                          </label>
                        ))
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Col 3: Run Action */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Generate Statements</CardTitle>
                <CardDescription>
                  {months.find(m => m.value === periodMonth)?.label} {periodYear} - {runTarget === 'clients' ? 'Client' : 'Investor'} Statements
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Summary */}
                <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Period:</span>
                    <span className="font-medium">{months.find(m => m.value === periodMonth)?.label} {periodYear}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Type:</span>
                    <span className="font-medium capitalize">{statementType}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Target:</span>
                    <span className="font-medium capitalize">{runTarget}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Recipients:</span>
                    <span className="font-medium">
                      {selectionMode === 'all'
                        ? `All (${currentUserList.length})`
                        : `${selectedUserIds.length} selected`}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Template:</span>
                    <span className="font-medium truncate max-w-[140px]">
                      {selectableTemplates.find(t => t.id === selectedTemplateId)?.name || 'No template selected'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Notify:</span>
                    <span className="font-medium">{sendNotifications ? 'Yes' : 'No'}</span>
                  </div>
                </div>

                <Button 
                  onClick={handleRunStatements}
                  disabled={loading || (selectionMode === 'selected' && selectedUserIds.length === 0)}
                  className={`w-full justify-center gap-3 h-14 ${
                    runTarget === 'clients'
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                  data-testid="run-statements-btn"
                >
                  {runningBatch ? (
                    <RefreshCw className="h-5 w-5 animate-spin" />
                  ) : (
                    <Play className="h-5 w-5" />
                  )}
                  <span className="font-semibold">
                    {loading ? 'Generating...' : `Generate ${runTarget === 'clients' ? 'Client' : 'Investor'} Statements`}
                  </span>
                </Button>

                {selectionMode === 'selected' && selectedUserIds.length === 0 && (
                  <p className="text-xs text-amber-600 text-center">
                    Select at least one {runTarget === 'clients' ? 'client' : 'investor'} to continue
                  </p>
                )}

                {/* Last Runs */}
                {stats?.last_runs && (
                  <div className="mt-4 p-3 bg-slate-50 rounded-lg text-sm">
                    <p className="font-medium text-slate-700 mb-2">Last Runs:</p>
                    {stats.last_runs.client_statements && (
                      <p className="text-slate-600">
                        <span className="font-medium">Client:</span> {stats.last_runs.client_statements.period} - {stats.last_runs.client_statements.successful} generated
                      </p>
                    )}
                    {stats.last_runs.investor_statements && (
                      <p className="text-slate-600">
                        <span className="font-medium">Investor:</span> {stats.last_runs.investor_statements.period} - {stats.last_runs.investor_statements.successful} generated
                      </p>
                    )}
                    {!stats.last_runs.client_statements && !stats.last_runs.investor_statements && (
                      <p className="text-slate-500 italic">No previous runs found</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Sanity Check — pick any investor + period, eyeball the
                derived statement numbers vs raw inputs without
                generating a PDF. Catches logic regressions early. */}
            <StatementSanityCheckPanel
              API_URL={API_URL}
              headers={{ Authorization: `Bearer ${token}` }}
            />
          </div>
        </TabsContent>

        {/* ====== History Tab ====== */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Statement Run History</CardTitle>
              <CardDescription>Recent batch statement generation runs</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Successful</TableHead>
                    <TableHead>Failed</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-slate-500 py-8">No statement runs found</TableCell>
                    </TableRow>
                  ) : (
                    history.map((run) => (
                      <TableRow key={run.id}>
                        <TableCell className="font-medium">{formatDate(run.run_at)}</TableCell>
                        <TableCell>
                          <Badge variant={run.batch_type === 'client_statements' ? 'default' : 'secondary'}>
                            {run.batch_type === 'client_statements' ? 'Clients' : 'Investors'}
                          </Badge>
                        </TableCell>
                        <TableCell>{run.period_start?.substring(0, 7)}</TableCell>
                        <TableCell>{run.total_processed}</TableCell>
                        <TableCell className="text-green-600 font-medium">{run.successful}</TableCell>
                        <TableCell className={run.failed > 0 ? 'text-red-600 font-medium' : ''}>{run.failed}</TableCell>
                        <TableCell>
                          {run.failed === 0 ? (
                            <Badge className="bg-green-100 text-green-800">
                              <CheckCircle className="h-3 w-3 mr-1" />Complete
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-800">
                              <AlertCircle className="h-3 w-3 mr-1" />Partial
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ====== Client Statements Tab ====== */}
        <TabsContent value="clients">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Client Statements</CardTitle>
                <CardDescription>Generated statements for clients/borrowers</CardDescription>
              </div>
              {selectedClientStmts.length > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleBulkDelete('client')}
                  data-testid="bulk-delete-client-stmts"
                  className="gap-1"
                >
                  <XCircle className="h-4 w-4" />
                  Delete {selectedClientStmts.length} Selected
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={clientStatements.length > 0 && selectedClientStmts.length === clientStatements.length}
                        onCheckedChange={() => toggleAllStmts('client')}
                        data-testid="select-all-client-stmts"
                      />
                    </TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Template</TableHead>
                    <TableHead>Outstanding</TableHead>
                    <TableHead>Generated</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clientStatements.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-slate-500 py-8">No client statements found</TableCell>
                    </TableRow>
                  ) : (
                    clientStatements.map((stmt) => (
                      <TableRow key={stmt.id} className={selectedClientStmts.includes(stmt.id) ? 'bg-blue-50' : ''}>
                        <TableCell>
                          <Checkbox
                            checked={selectedClientStmts.includes(stmt.id)}
                            onCheckedChange={() => toggleStmtSelection(stmt.id, 'client')}
                            data-testid={`select-client-stmt-${stmt.id}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium">
                          <div>
                            <div>{stmt.client_name || stmt.client_id}</div>
                            <div className="text-xs text-slate-500">{stmt.client_id}</div>
                          </div>
                        </TableCell>
                        <TableCell>{stmt.period_label}</TableCell>
                        <TableCell><Badge variant="outline">{stmt.statement_type}</Badge></TableCell>
                        <TableCell className="text-xs">{stmt.template_name || 'Default PDF'}</TableCell>
                        <TableCell>{formatCurrency(stmt.summary?.total_outstanding)}</TableCell>
                        <TableCell className="text-sm text-slate-600">{formatDate(stmt.generated_at)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {stmt.has_html && (
                              <Button size="sm" variant="outline" onClick={() => handleView(stmt.id, 'client')} className="gap-1" data-testid={`view-client-stmt-${stmt.id}`}>
                                <FileText className="h-3 w-3" />View
                              </Button>
                            )}
                            <Button size="sm" variant="outline" onClick={() => handleDownload(stmt.id, 'client')} className="gap-1" data-testid={`download-client-stmt-${stmt.id}`}>
                              <Download className="h-3 w-3" />PDF
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDelete(stmt.id, 'client')} className="gap-1 text-red-500 hover:text-red-700" data-testid={`delete-client-stmt-${stmt.id}`}>
                              <XCircle className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ====== Investor Statements Tab ====== */}
        <TabsContent value="investors">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Investor Statements</CardTitle>
                <CardDescription>Generated statements for investors</CardDescription>
              </div>
              {selectedInvestorStmts.length > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleBulkDelete('investor')}
                  data-testid="bulk-delete-investor-stmts"
                  className="gap-1"
                >
                  <XCircle className="h-4 w-4" />
                  Delete {selectedInvestorStmts.length} Selected
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={investorStatements.length > 0 && selectedInvestorStmts.length === investorStatements.length}
                        onCheckedChange={() => toggleAllStmts('investor')}
                        data-testid="select-all-investor-stmts"
                      />
                    </TableHead>
                    <TableHead>Investor</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Template</TableHead>
                    <TableHead>Portfolio Value</TableHead>
                    <TableHead>Generated</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {investorStatements.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-slate-500 py-8">No investor statements found</TableCell>
                    </TableRow>
                  ) : (
                    investorStatements.map((stmt) => (
                      <TableRow key={stmt.id} className={selectedInvestorStmts.includes(stmt.id) ? 'bg-blue-50' : ''}>
                        <TableCell>
                          <Checkbox
                            checked={selectedInvestorStmts.includes(stmt.id)}
                            onCheckedChange={() => toggleStmtSelection(stmt.id, 'investor')}
                            data-testid={`select-investor-stmt-${stmt.id}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{stmt.investor_name || stmt.user_id}</TableCell>
                        <TableCell>{stmt.period_label}</TableCell>
                        <TableCell><Badge variant="outline">{stmt.statement_type}</Badge></TableCell>
                        <TableCell className="text-xs">{stmt.template_name || 'Default PDF'}</TableCell>
                        <TableCell>{formatCurrency(stmt.summary?.closing_balance)}</TableCell>
                        <TableCell className="text-sm text-slate-600">{formatDate(stmt.generated_at)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {stmt.has_html && (
                              <Button size="sm" variant="outline" onClick={() => handleView(stmt.id, 'investor')} className="gap-1" data-testid={`view-investor-stmt-${stmt.id}`}>
                                <FileText className="h-3 w-3" />View
                              </Button>
                            )}
                            <Button size="sm" variant="outline" onClick={() => handleDownload(stmt.id, 'investor')} className="gap-1" data-testid={`download-investor-stmt-${stmt.id}`}>
                              <Download className="h-3 w-3" />PDF
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDelete(stmt.id, 'investor')} className="gap-1 text-red-500 hover:text-red-700" data-testid={`delete-investor-stmt-${stmt.id}`}>
                              <XCircle className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminStatementsPage;
